import React from 'react'
import { t } from 'ttag'

import { CandidateManagementModal } from 'components/modals/CandidateManagementModal/CandidateManagementModal'
import { DeleteCandidateModal } from 'components/modals/DeleteCandidateModal/DeleteCandidateModal'
import { MoveCandidateModal } from 'components/modals/MoveCandidateModal/MoveCandidateModal'
import { SelectCandidateModal } from 'components/modals/SelectCandidateModal/SelectCandidateModal'
import { TestsReportModal } from 'components/modals/TestsReportModal/TestsReportModal'
import { DeleteCandidateGroupModal } from 'components/modals/DeleteCandidateGroupModal/DeleteCandidateGroupModal'
import { ExportCandidateGroupModal } from 'components/modals/ExportCandidateGroupModal/ExportCandidateGroupModal'
import { MoveCandidateGroupModal } from 'components/modals/MoveCandidateGroupModal/MoveCandidateGroupModal'
import { SelectCandidateGroupModal } from 'components/modals/SelectCandidateGroupModal/SelectCandidateGroupModal'
import { ActionsDropdown } from 'components/shared/ActionsDropdown/ActionsDropdown'
import { Button } from 'components/shared/Button/Button'
import { Pane } from 'components/shared/Pane/Pane'
import { SearchWidget } from 'components/shared/SearchWidget/SearchWidget'
import { TestsTable } from './common/TestsTable/TestsTable'
import { FilterDropdown } from 'components/shared/FilterDropdown/FilterDropdown'
import { getFiltersConfigs } from '../model'
import { useJobTestsTab } from './hooks'
import { PaneTitle } from '../PaneTitle/PaneTitle'

import { UserCheckIcon } from 'components/shared/Icon/model'
import { CompareButton } from '../CompareButton/CompareButton'

import styles from './Tests.module.scss'

export const Tests = ({ activeTab, project, hasInterviewModule }) => {
  const { id, name, active, modules } = project ?? {}

  const { state, handlers } = useJobTestsTab({ projectId: id })

  const {
    actions,
    candidate,
    count,
    search,
    filter,
    selectedCandidates,
    hasCandidatesSelected,
    hasTwoCandidatesSelected,
    hasMoveCandidatesFeature,
    isCandidateManagementModalOpen,
    isDeleteCandidateModalOpen,
    isMoveCandidateModalOpen,
    isSelectCandidateModalOpen,
    isTestsReportModalOpen,
    isDeleteCandidateGroupModalOpen,
    isExportCandidateGroupModalOpen,
    isMoveCandidateGroupModalOpen,
    isSelectCandidateGroupModalOpen,
  } = state
  const {
    getActions,
    handleFavorite,
    handleCandidateManagementModalClose,
    handleCandidateManagementModalConfirm,
    handleDeleteCandidateModalClose,
    handleDeleteCandidateModalConfirm,
    handleMoveCandidateModalClose,
    handleMoveCandidateModalConfirm,
    handleSelectCandidateModalClose,
    handleSelectCandidateModalConfirm,
    handleTestsReportModalClose,
    handleDeleteCandidateGroupModalClose,
    handleDeleteCandidateGroupModalConfirm,
    handleExportCandidateGroupModalClose,
    handleMoveCandidateGroupModalClose,
    handleMoveCandidateGroupModalConfirm,
    handleSelectCandidateGroupModalClose,
    handleSelectCandidateGroupModalConfirm,
    handleSelectCandidateGroupModalOpen,
    handleSelectForInterview,
    handleTestsReport,
    handleCompare,
    setSearch,
    setFilter,
  } = handlers

  return (
    <>
      <Pane.Header
        direction="column"
        subtitle={`${count} ${t`candidates`}`}
        title={<PaneTitle label={name} active={active} />}
      >
        <SearchWidget query={search} onChange={setSearch} />
        <FilterDropdown
          size="small"
          filter={filter}
          setFilter={setFilter}
          filtersConfig={getFiltersConfigs()}
        />
        <CompareButton
          isDisabled={!hasTwoCandidatesSelected}
          handleClick={handleCompare}
        />
        {modules?.interview ? (
          <Button
            className={styles.button}
            color="primary"
            icon={UserCheckIcon}
            label={t`Move to interview`}
            size="small"
            variant="filled"
            onClick={handleSelectCandidateGroupModalOpen}
            isDisabled={!hasCandidatesSelected}
          />
        ) : null}
        <ActionsDropdown
          anchorClassName={styles.button}
          size="small"
          isDisabled={!active}
          {...{ actions }}
        />
      </Pane.Header>
      <TestsTable
        projectId={id}
        tab={activeTab}
        onSelectForInterview={handleSelectForInterview}
        onTestsReport={handleTestsReport}
        {...{
          search,
          filter,
          setFilter,
          getActions,
          hasInterviewModule,
          handleFavorite,
          active,
          modules,
        }}
      />
      <SelectCandidateGroupModal
        onCancel={handleSelectCandidateGroupModalClose}
        onClose={handleSelectCandidateGroupModalClose}
        onConfirm={handleSelectCandidateGroupModalConfirm}
        isOpen={isSelectCandidateGroupModalOpen}
      />
      {hasMoveCandidatesFeature ? (
        <MoveCandidateGroupModal
          onCancel={handleMoveCandidateGroupModalClose}
          onClose={handleMoveCandidateGroupModalClose}
          onConfirm={handleMoveCandidateGroupModalConfirm}
          isOpen={isMoveCandidateGroupModalOpen}
        />
      ) : null}
      <ExportCandidateGroupModal
        onCancel={handleExportCandidateGroupModalClose}
        onClose={handleExportCandidateGroupModalClose}
        isOpen={isExportCandidateGroupModalOpen}
      />
      <DeleteCandidateGroupModal
        onCancel={handleDeleteCandidateGroupModalClose}
        onClose={handleDeleteCandidateGroupModalClose}
        onConfirm={handleDeleteCandidateGroupModalConfirm}
        isOpen={isDeleteCandidateGroupModalOpen}
      />
      {isTestsReportModalOpen ? (
        <TestsReportModal
          onCancel={handleTestsReportModalClose}
          onClose={handleTestsReportModalClose}
          isOpen={isTestsReportModalOpen}
          project={project}
          candidates={candidate ? [candidate] : selectedCandidates}
        />
      ) : null}
      <SelectCandidateModal
        name={candidate?.name}
        onCancel={handleSelectCandidateModalClose}
        onClose={handleSelectCandidateModalClose}
        onConfirm={handleSelectCandidateModalConfirm}
        isOpen={isSelectCandidateModalOpen}
      />
      <CandidateManagementModal
        onCancel={handleCandidateManagementModalClose}
        onClose={handleCandidateManagementModalClose}
        onConfirm={handleCandidateManagementModalConfirm}
        isOpen={isCandidateManagementModalOpen}
        {...{ candidate }}
      />
      {hasMoveCandidatesFeature ? (
        <MoveCandidateModal
          name={candidate?.name}
          onCancel={handleMoveCandidateModalClose}
          onClose={handleMoveCandidateModalClose}
          onConfirm={handleMoveCandidateModalConfirm}
          isOpen={isMoveCandidateModalOpen}
        />
      ) : null}
      <DeleteCandidateModal
        name={candidate?.name}
        onCancel={handleDeleteCandidateModalClose}
        onClose={handleDeleteCandidateModalClose}
        onConfirm={handleDeleteCandidateModalConfirm}
        isOpen={isDeleteCandidateModalOpen}
      />
    </>
  )
}
